import Pusher, { Channel } from "pusher-js";
import { CookieComponent } from "~/composable/CookieComponent";
import { PusherConfig } from "~/types/pusherConfig";
import { useAuthStore } from "~/stores/auth";
import { useConfigStore } from "~/stores/configStore";
import { useStatusStore } from "~/stores/statusStore";

const statusStore = useStatusStore();
const configStore = useConfigStore();

const authStore = useAuthStore();

Pusher.logToConsole = false;

export class EventService {
  // eslint-disable-next-line no-use-before-define
  public static instance: EventService | null = null;

  private pusher: Pusher | null = null;

  // channels
  private calls: Channel | null = null;
  private tickets: Channel | null = null;
  private convs: Channel | null = null;
  private tasks: Channel | null = null;
  private notifications: Channel | null = null;
  private followUps: Channel | null = null;

  constructor() {
    if (EventService.instance === null) {
      const pusherConfig = configStore._globalConfig.pusher_config;
      const { $event } = useNuxtApp();
      const user = authStore.user;
      const broadcastUrl =
        useRuntimeConfig().public.APP_BASE_URL + "/broadcasting/auth";

      this.pusher = new Pusher(pusherConfig.key, {
        cluster: "",
        channelAuthorization: {
          transport: "ajax",
          endpoint: broadcastUrl,
          // A function, not a fixed object, because Pusher re-authorizes on every
          // resubscribe — i.e. on every reconnect. The old `auth.headers` froze both
          // the bearer token and the XSRF cookie at construction, so once either
          // rotated (a password reset, a session regeneration) every resubscribe 401'd
          // and the socket went quiet for the rest of the session. Reading them here
          // means a reconnect always presents current credentials.
          headersProvider: () => ({
            Accept: "application/json",
            "X-CSRF-TOKEN": CookieComponent.get("XSRF-TOKEN"),
            Authorization: "Bearer " + authStore.token,
          }),
        },
        wsHost: pusherConfig.options.host,
        wsPort: pusherConfig.options.port,
        wssPort: pusherConfig.options.port,
        forceTLS: pusherConfig.options.useTLS,
        enableStats: true,
        enabledTransports: ["ws", "wss"],
      });

      // channels
      this.calls = this.pusher.subscribe("private-calls");
      this.tickets = this.pusher.subscribe("private-tickets");
      this.convs = this.pusher.subscribe(`private-convs.${user.id}`);
      this.tasks = this.pusher.subscribe(`private-tasks.${user.id}`);
      this.notifications = this.pusher.subscribe(
        `private-notifications.${user.id}`,
      );
      this.followUps = this.pusher.subscribe(`private-follow-ups.${user.id}`);

      // Track Pusher connection state
      this.pusher.connection.bind("state_change", (states: any) => {
        statusStore.setConnectionStatus(states.current);
      });

      this.pusher.connection.bind("disconnected", () => {
        statusStore.setConnectionStatus("Disconnected");
      });

      this.pusher.connection.bind("failed", () => {
        statusStore.setConnectionStatus("Failed");
      });

      // Events that fired while the socket was down are gone — Pusher has no replay.
      // Any list built from those events is now missing rows, so record that a gap
      // happened and let the UI offer a reload. Cleared by whoever acts on it.
      this.pusher.connection.bind("connected", () => {
        statusStore.setConnectionStatus("Connected");
      });

      this.notifications.bind("pusher:subscription_succeeded", () => {
        statusStore.setConnectionStatus("Connected");
      });

      this.notifications.bind("pusher:subscription_error", () => {
        statusStore.setConnectionStatus("Disconnected");
      });

      // events

      this.calls.bind("uh_call_created", (d) => $event("uh_call_created", d));
      this.calls.bind("uh_calls_handled", (d) => $event("uh_calls_handled", d));
      this.calls.bind("call_created", (d) => $event("call_created", d));
      this.calls.bind("call_updated", (d) => $event("call_updated", d));

      this.tickets.bind("ticket_assigned", (d) => $event("ticket_assigned", d));
      this.tickets.bind("ticket_created", (d) => $event("ticket_created", d));
      this.tickets.bind("ticket_unpicked", (d) => $event("ticket_unpicked", d));
      this.tickets.bind("meta_user_updated", (d) =>
        $event("meta_user_updated", d),
      );
      this.convs.bind("conv_created", (d) => $event("conv_created", d));
      this.convs.bind("conv_updated", (d) => $event("conv_updated", d));
      this.convs.bind("ticket_convs_read", (d) =>
        $event("ticket_convs_read", d),
      );
      // Ticket changed in a way no other event covers — see TicketUpdated.php.
      // Per-agent, so it rides the convs channel rather than the global one.
      this.convs.bind("ticket_updated", (d) => $event("ticket_updated", d));

      this.tasks.bind("task_created", (d) => $event("task_created", d));
      this.tasks.bind("task_reminder", (d) => $event("task_reminder", d));

      this.notifications.bind("notification_created", (d) =>
        $event("notification_created", d),
      );

      /*
       * Support conversations, which live in the billing app.
       *
       * It has no socket to this browser, so it calls our backend and that
       * broadcasts here — on this same channel, so there is nothing extra to
       * subscribe to or authorise. The widget used to ask the billing app every
       * few seconds instead, from every agent, whether or not anyone was talking.
       */
      this.notifications.bind("support_message_received", (d) =>
        $event("support_message_received", d),
      );
      // A message they already have, corrected or withdrawn. Separate from
      // "received" because the widget replaces rather than appends.
      this.notifications.bind("support_message_changed", (d) =>
        $event("support_message_changed", d),
      );
      this.notifications.bind("support_conversation_read", (d) =>
        $event("support_conversation_read", d),
      );
      this.notifications.bind("support_updates_changed", (d) =>
        $event("support_updates_changed", d),
      );

      // Per-agent: a follow-up belongs to exactly one agent, and the server only
      // broadcasts to that agent's channel, so unlike the global tickets channel
      // there is no client-side "is it mine" filtering to do here.
      this.followUps.bind("follow_up_assigned", (d) =>
        $event("follow_up_assigned", d),
      );
      this.followUps.bind("follow_up_removed", (d) =>
        $event("follow_up_removed", d),
      );

      this.pusher.bind_global((e) => {
        console.log(e);
      });

      EventService.instance = this;
    }
    return EventService.instance;
  }

  dispose() {
    EventService.instance?.pusher?.disconnect();
    EventService.instance = null;
  }
}
